/**
 * Was „Erwachsene" und „Kinder" bei der Belegung wirklich bedeuten.
 *
 * Die beiden Zahlen gehen unverändert als `occ_adults`/`occ_children` an Channex, und dort sind es
 * **Schlafplätze, die sich addieren** — nicht zwei Obergrenzen unter einer Gesamtzahl, wie es das
 * Booking.com-Modell nahelegt. Channex' eigene Erklärung: „Adult beds can sleep adults and
 * children, child beds are for children only."
 *
 * Für eine Wohnung mit neun Schlafplätzen gehören deshalb 9 und 0 eingetragen. 9 und 8 ergeben
 * siebzehn — genau der Fall, der live passiert ist. Die Summe steht darum sichtbar am Feld, bevor
 * irgendetwas veröffentlicht wird.
 */
export function SleepingSpacesHint({
  adults,
  childSpaces,
}: {
  adults: number;
  // Nicht `children`: das ist in React ein Sonderfeld, und wer die Komponente spaeter mit
  // Inhalt umschliesst, wuerde die Zahl stillschweigend ueberschreiben.
  childSpaces: number;
}) {
  const total = (Number(adults) || 0) + (Number(childSpaces) || 0);
  const hasChildSpaces = (Number(childSpaces) || 0) > 0;

  return (
    <div
      className={
        hasChildSpaces
          ? 'border-sealwax/30 bg-sealwax/5 text-ink rounded-md border p-3 text-sm'
          : 'border-line/60 bg-card-alt/40 text-ink-soft rounded-md border p-3 text-sm'
      }
      role="status"
    >
      <p className="text-ink font-medium">
        Ergibt {total} {total === 1 ? 'Schlafplatz' : 'Schlafplätze'} bei den Portalen.
      </p>
      <p className="mt-1">
        Die beiden Zahlen addieren sich. Erwachsenen-Plätze nehmen auch Kinder auf, Kinder-Plätze
        nur Kinder — wer insgesamt {total === 1 ? 'eine Person' : `${total} Personen`} unterbringen
        kann, trägt diese Zahl bei den Erwachsenen ein und lässt die Kinder auf 0.
      </p>
    </div>
  );
}

export const SLEEPING_SPACE_LABELS = {
  adults: 'Erwachsenen-Schlafplätze',
  children: 'Kinder-Schlafplätze',
  adultsHint: 'Nehmen auch Kinder auf.',
  childrenHint: 'Nur für Kinder. Im Zweifel 0.',
} as const;
